// src/pages/Download.tsx

import React, { useState, useEffect, useContext, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import TermsAcceptance from '../components/auth/TermsAcceptance';
import { useNavigate, Link } from 'react-router-dom';
import { NotificationContext } from '../contexts/NotificationContext';
import { jwtDecode } from "jwt-decode";
import { Helmet } from 'react-helmet-async';
import ParticleBackground from '../components/layout/ParticleBackground';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DownloadContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: transparent;
  color: var(--color-text);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 8rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 6rem 1.5rem;
  }
`;

const Title = styled.h1`
  color: var(--color-text);
  font-size: 3.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideDownFadeIn} 0.8s ease-out forwards;
  opacity: 0;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: 1px;
  }
`;

const Description = styled.div`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: var(--color-text);
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out 0.3s forwards;
  opacity: 0;
  font-family: 'Montserrat', Arial, sans-serif;

  p {
    margin-bottom: 1.5rem;
  }

  a {
    color: var(--color-primary);
    text-decoration: underline;
    text-decoration-color: var(--color-primary);
    text-underline-offset: 3px;
    transition: all 0.3s ease;
    font-weight: 500;

    &:hover {
      color: var(--color-secondary);
      text-decoration-color: var(--color-secondary);
      text-underline-offset: 2px;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

/* New styled component to highlight NinjaTrader & RiskMaster version info */
const VersionHighlight = styled.div`
  background-color: var(--color-card-bg);
  padding: 1.5rem 2rem;
  color: var(--color-text);
  font-weight: bold;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
  animation: ${slideUpFadeIn} 0.8s ease-out 0.6s forwards;
  opacity: 0;
  font-family: 'Montserrat', Arial, sans-serif;
  border: 1px solid var(--color-primary);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DownloadButtonStyled = styled.button`
  background: transparent;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: 1rem 2.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideUpFadeIn} 0.8s ease-out 0.9s forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;
  font-family: 'Montserrat', Arial, sans-serif;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: var(--color-primary);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1.5rem;
  }
`;

interface DownloadProps {
  isLoggedIn: boolean;
  onLogout: () => void;
}

const Download: React.FC<DownloadProps> = ({ isLoggedIn, onLogout }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const isTokenValid = (token: string | null) => {
    if (!token) return false;
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp && decoded.exp > currentTime;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return false;
    }
  };

  const fetchUserData = useCallback(async (token: string) => {
    try {
      const response = await fetch('https://riskmaster.pro/fetch_user_data_proxy.php', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        // showNotification('Session expired. Please log in again.');
        navigate('/');
        return;
      }

      const data = await response.json();
      if (data.status === 'success') {
        setUserData(data.user);
      } else {
        showNotification(data.message || 'Error fetching user data.');
      }
    } catch (error) {
      showNotification('Failed to fetch user data.');
      console.error(error);
    }
  }, [navigate, showNotification]);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token && !isTokenValid(token)) {
      localStorage.removeItem('token');
      // showNotification('Session expired. Please log in again.');
      navigate('/');
      return;
    }

    if (token) {
      fetchUserData(token);
    }
  }, [navigate, showNotification, fetchUserData]);

  const handleDownloadClick = () => {
    const token = localStorage.getItem('token');

    if (token && !isTokenValid(token)) {
      localStorage.removeItem('token');
      // showNotification('Session expired. Please log in again.');
      navigate('/');
      return;
    }

    if (!token) {
      showNotification('Please login to download.');
      return;
    }

    if (userData) {
      setShowTerms(true);

      if (window.fbq) {
        window.fbq('track', 'DownloadAttempt');
      }
    }
  };

  const handleTermsAccepted = () => {
    setShowTerms(false);

    if (window.fbq) {
      window.fbq('track', 'DownloadInitiated');
    }

    window.location.href = 'https://drive.google.com/file/d/1aZXHp9XSwZiAUptZM_Ojj1HHbOKE7ppk/view?usp=sharing';
  };

  const handleExit = () => {
    setShowTerms(false);
  };

  return (
    <DownloadContainer>
      <Helmet>
        <title>Download Risk Master | RiskMaster</title>
        <meta
          name="description"
          content="Download the Risk Master software to enhance your trading strategy with automated risk management tools."
        />
        <link rel="canonical" href="https://www.riskmaster.net/download" />
      </Helmet>
      <ParticleBackground />
      <Navigation
        activeSection="download"
        handleScroll={() => {}}
        isLoggedIn={isLoggedIn}
        onLogout={onLogout}
      />
      <ContentWrapper>
        <Title>Download Risk Master</Title>
        <Description>
          <p>
            Download the latest version of Risk Master to start your journey towards consistent trading.
          </p>
          <p>
            Please make sure you meet the system requirements for NinjaTrader 8 and have read or seen the installation guide in the{' '}
            <Link to="/help-center">
              Help Center
            </Link>.
          </p>
        </Description>

        {/* Highlight the recommended NinjaTrader version and RiskMaster version */}
        <VersionHighlight>
          We strongly recommend using the latest version of NinjaTrader (8.1.3.1 64-bit or higher). 
          <br />
          Current Risk Master version: <strong>1.3.0.1</strong>
        </VersionHighlight>

        <DownloadButtonStyled onClick={handleDownloadClick}>
          Download Now
        </DownloadButtonStyled>
      </ContentWrapper>
      {showTerms && userData && (
        <TermsAcceptance
          userId={userData.id}
          version="1.0"
          onAccepted={handleTermsAccepted}
          onExit={handleExit}
        />
      )}
      <Footer />
    </DownloadContainer>
  );
};

export default Download;
